import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Route, MapPin, Calendar, Clock, ChevronLeft, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom marker for start point
const startIcon = L.divIcon({
  className: 'custom-marker',
  html: `<div style="
    background-color: #22c55e;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    color: white;
    font-size: 12px;
  ">S</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Custom marker for end point
const endIcon = L.divIcon({
  className: 'custom-marker',
  html: `<div style="
    background-color: #ef4444;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    color: white;
    font-size: 12px;
  ">E</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Component to fit map bounds to route
function FitBounds({ positions }) {
  const map = useMap();
  
  useEffect(() => {
    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [positions, map]);
  
  return null;
}

export default function LocationHistoryDialog({ open, onClose }) {
  const [trackedUsers, setTrackedUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [view, setView] = useState('users'); // 'users' or 'map'

  const fetchTrackedUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/locations/tracked-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrackedUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching tracked users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserHistory = async (userId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/locations/history/${userId}?date=${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHistory(response.data.history || []);
      setView('map');
    } catch (error) {
      console.error('Error fetching user history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchTrackedUsers();
      setView('users');
      setSelectedUser(null);
      setHistory([]);
    }
  }, [open]);

  useEffect(() => {
    if (selectedUser) {
      fetchUserHistory(selectedUser.user_id);
    }
  }, [selectedDate, selectedUser]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateDistance = (positions) => {
    if (positions.length < 2) return 0;
    let total = 0;
    for (let i = 1; i < positions.length; i++) {
      const [lat1, lon1] = positions[i - 1];
      const [lat2, lon2] = positions[i];
      total += getDistanceInKm(lat1, lon1, lat2, lon2);
    }
    return total.toFixed(2);
  };

  const getDistanceInKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const positions = history.map(h => [h.latitude, h.longitude]);
  const defaultCenter = [40.7128, -74.0060];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh]" data-testid="location-history-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0f172a] flex items-center gap-2">
            <Route className="w-6 h-6 text-blue-500" />
            {view === 'users' ? 'Location History' : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setView('users')}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {selectedUser?.user_name}'s Route
              </div>
            )}
          </DialogTitle>
        </DialogHeader>

        {view === 'users' ? (
          <>
            {/* Users List */}
            <div className="text-sm text-slate-500 mb-2">
              Select a user to view their location history and route
            </div>
            
            <ScrollArea className="h-[500px]">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : trackedUsers.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-slate-500">
                  <Route className="w-8 h-8 mb-2" />
                  <p>No location history available</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {trackedUsers.map((user) => (
                    <div
                      key={user.user_id}
                      className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-blue-300 cursor-pointer transition-colors"
                      onClick={() => setSelectedUser(user)}
                      data-testid={`tracked-user-${user.user_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.user_role === 'manager' ? 'bg-blue-500' : 'bg-green-500'} text-white`}>
                            <MapPin className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800">{user.user_name}</p>
                            <p className="text-sm text-slate-500 capitalize">{user.user_role}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-600">{user.total_points} locations</p>
                          <p className="text-xs text-slate-500">
                            Last: {formatDate(user.last_location)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </>
        ) : (
          <>
            {/* Date Selector */}
            <div className="flex items-center gap-4 py-2">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                <Label className="text-sm">Date:</Label>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-40"
                  data-testid="history-date-picker"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchUserHistory(selectedUser.user_id)}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-6 py-2 px-4 bg-slate-50 rounded-lg text-sm">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-slate-500" />
                <span><strong>{history.length}</strong> points</span>
              </div>
              <div className="flex items-center gap-2">
                <Route className="w-4 h-4 text-slate-500" />
                <span><strong>{calculateDistance(positions)}</strong> km traveled</span>
              </div>
              {history.length > 0 && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-500" />
                  <span>{formatTime(history[0]?.timestamp)} - {formatTime(history[history.length - 1]?.timestamp)}</span>
                </div>
              )}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-green-500 border-2 border-white shadow flex items-center justify-center text-white text-[10px] font-bold">S</div>
                <span>Start</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-white shadow flex items-center justify-center text-white text-[10px] font-bold">E</div>
                <span>End</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-1 bg-blue-500 rounded"></div>
                <span>Route</span>
              </div>
            </div>

            {/* Map */}
            <div className="h-[400px] rounded-lg overflow-hidden border border-slate-200">
              {history.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center bg-slate-100 text-slate-500">
                  <Route className="w-12 h-12 mb-4 text-slate-400" />
                  <p className="text-lg font-medium">No route data for this date</p>
                  <p className="text-sm">Try selecting a different date</p>
                </div>
              ) : (
                <MapContainer
                  center={positions[0] || defaultCenter}
                  zoom={13}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <FitBounds positions={positions} />
                  
                  {/* Route line */}
                  <Polyline
                    positions={positions}
                    color="#3b82f6"
                    weight={4}
                    opacity={0.8}
                  />
                  
                  {/* Start marker */}
                  {positions.length > 0 && (
                    <Marker position={positions[0]} icon={startIcon}>
                      <Popup>
                        <div className="p-2">
                          <p className="font-bold text-green-600">Start Point</p>
                          <p className="text-sm">{formatTime(history[0]?.timestamp)}</p>
                        </div>
                      </Popup>
                    </Marker>
                  )}
                  
                  {/* End marker */}
                  {positions.length > 1 && (
                    <Marker position={positions[positions.length - 1]} icon={endIcon}>
                      <Popup>
                        <div className="p-2">
                          <p className="font-bold text-red-600">End Point</p>
                          <p className="text-sm">{formatTime(history[history.length - 1]?.timestamp)}</p>
                        </div>
                      </Popup>
                    </Marker>
                  )}
                </MapContainer>
              )}
            </div>
          </>
        )}

        <div className="flex justify-end pt-2">
          <Button onClick={onClose} data-testid="close-location-history">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
